import { colorGray } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Flex } from 'antd';
import * as Icon from 'react-feather';
import { useHasPermission } from '../auth';
import { TextEllipsis } from '../common/components';
import { TableOperationButton } from '../common/form/table/TableOperationButton';
import { Table, TableProps } from '../common/tanstacktable';
import { useSystemClassifierSelectOptions } from '../configuration';
import { PermissionCode } from '../user';
import translations from './translations/DataObjectTable.translations.json';
import { DataObjectType, DataObjectView } from './types';
import { FC, useMemo } from 'react';
import { DataObjectVersionSelect } from './DataObjectVersionSelect';
import { DataObjectDirectoryType } from './schema';

const { addTranslations, useTranslation } = i18nNsFactory('data-object-table');
addTranslations(translations);

const columnHelper = createColumnHelper<DataObjectDirectoryType>();

export interface DataObjectTableProps
  extends Omit<
    TableProps<DataObjectDirectoryType>,
    'name' | 'columns' | 'scrollContainer' | 'virtual'
  > {
  dataObjectView: DataObjectView;
  getRedirectAnchor: (versionId: number, value: string) => React.ReactNode;
  onClickCreate: () => void;
  onSelectVersion: (params: { id?: number }) => void;
}

export const DataObjectTable: FC<DataObjectTableProps> = (props) => {
  const {
    dataObjectView,
    getRedirectAnchor,
    onClickCreate,
    onSelectVersion,
    ...rest
  } = props;
  const { t } = useTranslation();
  const { dataObjectType } = dataObjectView;

  const hasPermissionToManageDataObjectType = useHasPermission(
    dataObjectType === DataObjectType.IN
      ? PermissionCode.INPUT_DESC_MANAGE
      : PermissionCode.OUTPUT_DESC_MANAGE,
  );

  const cols = useMemo(() => {
    const cols = [
      columnHelper.accessor('code', {
        header: t('column.code'),
        cell: (props) => (
          <TextEllipsis>
            {(props.row.original.activeVersion
              ? getRedirectAnchor(
                  props.row.original.activeVersion.id,
                  props.renderValue(),
                )
              : props.renderValue()) ?? ''}
          </TextEllipsis>
        ),
        enableColumnFilter: false,
        meta: {
          className: 'sticky-left',
          width: '30ch',
        },
      }),
      columnHelper.accessor('name', {
        header: t('column.name'),
        enableColumnFilter: false,
        meta: {
          width: '60%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('dataObjectGroupCode', {
        cell: (props) => (
          <TextEllipsis>{props.row.original.groupSubgroupName}</TextEllipsis>
        ),
        header: t('column.dataObjectGroupName.' + dataObjectType),
        enableColumnFilter: false,
        meta: {
          width: '40%',
          minWidth: 150,
        },
      }),
    ] as ColumnDef<DataObjectDirectoryType, any>[];

    if (dataObjectType === DataObjectType.OUT) {
      cols.push(
        columnHelper.accessor('outputTypeCode', {
          header: t('column.outputTypeCode.label'),
          cell: (props) => t('column.outputTypeCode.' + props.renderValue()),
          enableColumnFilter: false,
          meta: {
            className: 'sticky-left',
            width: 150,
          },
        }) as ColumnDef<DataObjectDirectoryType, any>,
      );
    }
    cols.push(
      columnHelper.accessor('frequencyName', {
        id: 'frequencyIds',
        header: t('column.frequency'),
        filterFn: 'includesOption',
        meta: {
          width: 120,
        },
      }),
    );
    if (dataObjectType === DataObjectType.IN) {
      cols.push(
        columnHelper.accessor('sourceNames', {
          id: 'originIds',
          header: t('column.sources'),
          filterFn: 'includesOption',
          meta: {
            width: '30ch',
          },
        }),
      );
    }
    cols.push(
      columnHelper.accessor('versions', {
        header: t('column.versions'),
        cell: (props) => (
          <DataObjectVersionSelect
            activeVersionId={props.row.original.activeVersion?.id}
            versions={props.row.original.versions}
            onChange={(id: any) => onSelectVersion({ id })}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: 270,
        },
      }) as ColumnDef<DataObjectDirectoryType, any>,
      columnHelper.accessor(
        (rowData) => rowData.activeVersion?.createdBy?.username ?? '-',
        {
          id: 'createdBy',
          header: t('column.createdBy'),
          enableColumnFilter: false,
          meta: {
            width: '15ch',
          },
        },
      ) as ColumnDef<DataObjectDirectoryType, any>,
    );

    if (hasPermissionToManageDataObjectType) {
      cols.push(
        columnHelper.display({
          id: 'operations',
          header: () => (
            <Flex justify="center" gap="small">
              <TableOperationButton
                id="create-dataObject"
                icon={<Icon.Plus size={22} color={colorGray[7]} />}
                onClick={() => onClickCreate()}
                title={t('button.add-data-object')}
              />
            </Flex>
          ),
          enableSorting: false,
          enableColumnFilter: false,
          meta: {
            width: 50,
            className: 'sticky-right',
          },
        }) as ColumnDef<DataObjectDirectoryType, any>,
      );
    }

    return cols;
  }, [
    dataObjectType,
    getRedirectAnchor,
    onClickCreate,
    onSelectVersion,
    t,
    hasPermissionToManageDataObjectType,
  ]);

  const systemClassifierOptions = useSystemClassifierSelectOptions(
    ['FREQUENCY', 'DATA_ORIGIN'],
    ({ id, name }) => ({ id, name }),
  );

  const tableFilterOptions = useMemo(
    () => ({
      frequencyIds: systemClassifierOptions.FREQUENCY,
      originIds: systemClassifierOptions.DATA_ORIGIN,
    }),
    [systemClassifierOptions],
  );

  return (
    <Table
      name="data-object-directory-table"
      columns={cols}
      hideClearFilters
      manualFiltering
      manualSorting
      scrollContainerSelector="#classifier-mapping-dir-content"
      sticky
      virtual
      options={tableFilterOptions}
      {...rest}
    />
  );
};
